import React, { useEffect, useState } from "react";
import { PAGE_TYPE } from "enums";
import { getProfileImageUrlByAccountTypeCode } from "helpers/Image.helper";
import { getLanguageValue } from "locales/i18n";
import LoadingComponent from "commons/loading/Loading.component";
import { IDiaryLike } from "api/interfaces/profile.interface";
import { getDiaryLikes } from "api/agency/v2/diary.api";
import { usePopup } from "contexts/PopupContext";
import StudentProfilePopup from "./StudentProfilePopup";
import { getCurrentLangName, getUserDisplayName } from "helpers/String.helper";
interface LikePopupForDiaryProps {
    diaryId: string;
    onClose: () => void;
}

const LikePopupForDiary: React.FC<LikePopupForDiaryProps> = ({ diaryId, onClose }) => {
    const { showPopup } = usePopup();
    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(20);
    const [list, setList] = useState<IDiaryLike[]>([]);
    const [isInit, setIsInit] = useState<boolean>(false);
    const [isOnLoadMore, setIsOnLoadMore] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const loadLikeList = async (isLoadMore: boolean = false) => {
        if (isOnLoadMore && !isLoadMore) return;

        try {
            if (isLoadMore) {
                setIsOnLoadMore(true);
            } else {
                setIsLoading(true);
            }

            const result = await getDiaryLikes(diaryId, { page, limit });

            if (result?.likeList) {
                if (isLoadMore) {
                    setList(prev => [...prev, ...result.likeList]);
                } else {
                    setList(result.likeList);
                }

                setHasMore(result.pagination?.hasNextPage || false);
                setIsInit(true);
            }
        } catch (error) {
            console.error(getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.likePopup.txtLoadError") + ":", error);
        } finally {
            setIsLoading(false);
            setIsOnLoadMore(false);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !isOnLoadMore) {
            handleLoadMore();
        }
    };

    const handleLoadMore = () => {
        if (!hasMore || isOnLoadMore) return;
        setPage(prev => prev + 1);
    };

    const handleShowStudentInfoPopup = (userStudentId: number) => {
        const popupContent = (
            <StudentProfilePopup
                userStudentId={userStudentId}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
    };

    const handleClose = () => {
        onClose();
    };

    useEffect(() => {
        if (diaryId) {
            loadLikeList();
        }
    }, [diaryId]);

    useEffect(() => {
        if (page > 1) {
            loadLikeList(true);
        }
    }, [page]);

    const isMyUser = (userId: string) => {
        // TODO: 현재 로그인한 사용자 ID와 비교
        return false;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 w-screen h-screen top-0 left-0 p-4 !m-0">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-auto max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.likePopup.txtTitle")}
                    </h3>
                    <button
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={handleClose}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {!isInit ? (
                    <div className="flex-1 overflow-y-auto mb-4">
                        <div className="space-y-3">
                            {/* 스켈레톤 UI */}
                            {[1, 2, 3, 4, 5].map((index) => (
                                <div key={`skeleton-${index}`} className="flex items-center justify-between p-3 animate-pulse">
                                    <div className="flex items-center space-x-3">
                                        {/* 프로필 이미지 스켈레톤 */}
                                        <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0"></div>
                                        <div className="min-w-0 flex-1 space-y-2">
                                            {/* 이름 스켈레톤 */}
                                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                                            {/* 계정 타입 스켈레톤 */}
                                            <div className="h-3 bg-gray-200 rounded w-16"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : list?.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center py-10 text-gray-500">
                        <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        <p className="text-center">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "diaryDetail.likePopup.txtNoLikes")}
                        </p>
                    </div>
                ) : (
                    <div
                        className="flex-1 overflow-y-auto mb-4"
                        onScroll={handleScroll}
                    >
                        <div className="space-y-3">
                            {list?.map((like, index) => (
                                <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <div
                                        className="flex items-center space-x-3 cursor-pointer"
                                        onClick={() => {
                                            if (like.user.userStudent?.id) {
                                                handleShowStudentInfoPopup(like.user.userStudent.id);
                                            }
                                        }}
                                    >
                                        <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                            <img
                                                src={getProfileImageUrlByAccountTypeCode(like.user, 40, 40)}
                                                alt={getUserDisplayName(like.user)}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = "/imgs/dummy/basic-img.png";
                                                }}
                                            />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-gray-900 truncate">
                                                {getUserDisplayName(like.user)}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {getCurrentLangName(like.user.accountType.names)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {isOnLoadMore && (
                                <div className="flex justify-center py-4">
                                    <LoadingComponent />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LikePopupForDiary;